import { useEffect, useState } from "react";
import { ThemeSelector } from "@/components/settings/ThemeSelector";
import { FontSelector } from "@/components/settings/FontSelector";
import { SlideThemeSelector } from "@/components/settings/SlideThemeSelector";
import { DocumentTypeManager } from "@/components/settings/DocumentTypeManager";
import { AISettings } from "@/components/settings/AISettings";
import { AccountSettings } from "@/components/settings/AccountSettings";
import { type SettingsSection } from "@/contexts/SettingsContext";
import { cn } from "@/lib/utils";
import { Palette, UserCircle, FileText, Sparkles, Presentation } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const NAV: {
  id: SettingsSection;
  label: string;
  icon: typeof Palette;
}[] = [
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "slides", label: "Slides", icon: Presentation },
  { id: "document-types", label: "Document types", icon: FileText },
  { id: "ai", label: "AI", icon: Sparkles },
  { id: "account", label: "Account", icon: UserCircle },
];

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultSection?: SettingsSection;
}

export function SettingsModal({
  isOpen,
  onClose,
  defaultSection = "appearance",
}: SettingsModalProps) {
  const [section, setSection] = useState<SettingsSection>(defaultSection);

  useEffect(() => {
    if (isOpen) setSection(defaultSection);
  }, [isOpen, defaultSection]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex h-[min(85vh,720px)] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 pr-12 text-left">
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Appearance, document types, AI, and account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
          {/* Mobile: horizontal strip; sm+: left nav */}
          <nav
            aria-label="Settings sections"
            className="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-3 py-2 sm:w-52 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:px-3 sm:py-3"
          >
            {NAV.map(({ id, label, icon: Icon }) => {
              const active = section === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSection(id)}
                  className={cn(
                    "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">{label}</span>
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {section === "appearance" && (
              <div className="space-y-8">
                <ThemeSelector />
                <div className="border-t border-border pt-6">
                  <FontSelector />
                </div>
              </div>
            )}
            {section === "slides" && <SlideThemeSelector />}
            {section === "document-types" && <DocumentTypeManager />}
            {section === "ai" && <AISettings />}
            {section === "account" && <AccountSettings />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
